import {
  NOTICE_DISCLOSURE_SCHEMA_VERSION,
  noticeDisclosureChangeDetail,
  optionalNoticeDisclosureId,
  parseNoticeDisclosureProjectionV1,
} from "./notice-disclosure-contract.js";
import {
  projectNoticeDisclosurePreference,
} from "./notice-disclosure-preference.js";
import {
  browserNoticeDisclosurePreferenceRepository,
} from "./notice-disclosure-preferences.js";

const DISCLOSURE_SELECTOR = "fishy-notice-disclosure[notice-id]";

function unavailableProjection(component) {
  return parseNoticeDisclosureProjectionV1({
    schemaVersion: NOTICE_DISCLOSURE_SCHEMA_VERSION,
    noticeId: optionalNoticeDisclosureId(component.noticeId),
    kind: "unavailable",
    revision: component.preferenceProjection?.revision ?? null,
    open: null,
    persisted: false,
    canPersist: false,
    warning: null,
  });
}

export function attachNoticeDisclosurePreferences(
  root = globalThis.document,
  { repository = null, pageTarget = globalThis.window ?? null } = {},
) {
  if (
    !root
    || typeof root.addEventListener !== "function"
    || typeof root.removeEventListener !== "function"
    || typeof root.querySelectorAll !== "function"
  ) {
    return Object.freeze({ dispose() {} });
  }
  const owner = repository ?? browserNoticeDisclosurePreferenceRepository();
  if (
    typeof owner?.setOpen !== "function"
    || typeof owner?.state !== "function"
    || typeof owner?.subscribe !== "function"
  ) {
    throw new TypeError("Notice disclosure controller requires its typed repository.");
  }

  const components = new Set();
  let currentState = owner.state();
  let disposed = false;

  function project(component) {
    if (!component?.isConnected) {
      components.delete(component);
      return;
    }
    try {
      const noticeId = optionalNoticeDisclosureId(component.noticeId);
      if (noticeId === null) return;
      component.preferenceProjection = projectNoticeDisclosurePreference(
        currentState,
        noticeId,
        Boolean(component.defaultOpen),
      );
    } catch (error) {
      globalThis.reportError?.(error);
    }
  }

  function add(component) {
    if (!component || typeof component !== "object") return;
    components.add(component);
    project(component);
  }

  function sync(state) {
    currentState = state;
    for (const component of components) project(component);
  }

  function handleReady(event) {
    add(event.target);
  }

  async function handleChange(event) {
    const component = event.target;
    try {
      const detail = noticeDisclosureChangeDetail(event.detail ?? {});
      if (optionalNoticeDisclosureId(component?.noticeId) !== detail.noticeId) return;
      add(component);
      await owner.setOpen(detail.noticeId, detail.open, {
        expectedRevision: detail.expectedRevision,
      });
    } catch (error) {
      try {
        component.preferenceProjection = unavailableProjection(component);
      } catch (_projectionError) {
        // An invalid notice ID has no persistence projection to own.
      }
      globalThis.reportError?.(error);
    }
  }

  function handlePageHide(event) {
    if (event?.persisted !== true) dispose();
  }

  root.addEventListener("fishy-notice-disclosure-ready", handleReady);
  root.addEventListener("fishy-notice-disclosure-change", handleChange);
  pageTarget?.addEventListener?.("pagehide", handlePageHide);
  for (const component of root.querySelectorAll(DISCLOSURE_SELECTOR)) add(component);
  const unsubscribe = owner.subscribe(sync);

  function dispose() {
    if (disposed) return;
    disposed = true;
    unsubscribe();
    components.clear();
    root.removeEventListener("fishy-notice-disclosure-ready", handleReady);
    root.removeEventListener("fishy-notice-disclosure-change", handleChange);
    pageTarget?.removeEventListener?.("pagehide", handlePageHide);
  }

  return Object.freeze({ dispose });
}
